import { periodNumberFor, periodRangeLabel } from '../../lib/payPeriods'
import type { CoverageReportRow } from './coverageReport'
import type { DetailRecord } from './detailedOutFigures'
import { matchesEmployee } from './employeeSearch'

/**
 * The overtime planning report (docs/product/reports.md): planned hours
 * beyond the normal schedule, by employee, date, shift, and assignment, with
 * the reason and the coverage gap the hours address, and totals by employee,
 * team, and pay period. Planning information. Pure, so the screen and the
 * exports read one result.
 */
export type OvertimeRecord = {
  id: string
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  team: string
  date: string
  shiftId: string | null
  shift: string
  assignmentId: string | null
  assignment: string
  hours: number
  reason: string | null
  // The category of the day's status code; a detail day reads as detailed out.
  statusCategory: string
}

export type OvertimeRow = OvertimeRecord & {
  supervisorId: string | null
  detailedOut: boolean
  periodNumber: number
  periodLabel: string
  // Headcount short of the target on that date, shift, and assignment; null
  // where no target applies.
  shortBy: number | null
  gap: string
}

export type OvertimeFilters = {
  teamId?: string
  supervisorId?: string
  shiftName?: string
  assignmentName?: string
  search?: string
  includeDetailedOut: boolean
}

// The coverage row an overtime entry belongs to: the same date, team, and
// assignment, and the same shift where the entry names one. Where several
// rows qualify, the one furthest below its target is the gap the hours serve.
function coverageFor(record: OvertimeRecord, coverage: CoverageReportRow[]): CoverageReportRow | undefined {
  const candidates = coverage.filter(
    (row) =>
      row.date === record.date &&
      row.teamId === record.teamId &&
      row.assignmentId === record.assignmentId &&
      row.assignment !== 'Whole shift' &&
      (!record.shiftId || row.shiftId === record.shiftId),
  )
  return candidates.reduce<CoverageReportRow | undefined>(
    (worst, row) => (!worst || (row.required ?? 0) - row.available > (worst.required ?? 0) - worst.available ? row : worst),
    undefined,
  )
}

export function buildOvertimeRows(
  records: OvertimeRecord[],
  coverage: CoverageReportRow[],
  details: DetailRecord[],
  supervisors: Map<string, string>,
): OvertimeRow[] {
  return records.map((record) => {
    const row = coverageFor(record, coverage)
    const shortBy = !row || row.required === null ? null : Math.max(0, row.required - row.available)
    const periodNumber = periodNumberFor(record.date)
    return {
      ...record,
      // An entry that names no shift takes the shift of the coverage row it
      // matched.
      shift: record.shift || row?.shift || 'No shift',
      supervisorId: supervisors.get(record.teamId) ?? null,
      detailedOut:
        record.statusCategory === 'detail' ||
        details.some((detail) => detail.employeeId === record.employeeId && detail.startDate <= record.date && record.date <= detail.endDate),
      periodNumber,
      periodLabel: `Pay Period ${periodNumber}, ${periodRangeLabel(periodNumber)}`,
      shortBy,
      gap:
        !row || row.required === null
          ? 'No target'
          : shortBy === 0
            ? 'No gap'
            : `Short ${shortBy} (${row.available} of ${row.required} available)`,
    }
  })
}

export function filterOvertime(rows: OvertimeRow[], filters: OvertimeFilters): OvertimeRow[] {
  return rows.filter(
    (row) =>
      (filters.includeDetailedOut || !row.detailedOut) &&
      (!filters.teamId || row.teamId === filters.teamId) &&
      (!filters.supervisorId || row.supervisorId === filters.supervisorId) &&
      (!filters.shiftName || row.shift === filters.shiftName) &&
      (!filters.assignmentName || row.assignment === filters.assignmentName) &&
      (!filters.search || matchesEmployee(filters.search, row.employee, row.identifier)),
  )
}

export type OvertimeGrouping = 'employee' | 'team' | 'period'

export type OvertimeTotal = {
  key: string
  label: string
  entries: number
  hours: number
  // Hours planned against a date, shift, and assignment that was short of
  // its target.
  gapHours: number
}

export function totalHours(rows: OvertimeRow[]): number {
  return rows.reduce((sum, row) => sum + row.hours, 0)
}

export function totalsBy(rows: OvertimeRow[], grouping: OvertimeGrouping): OvertimeTotal[] {
  const groups = new Map<string, OvertimeTotal>()
  for (const row of rows) {
    const [key, label] =
      grouping === 'employee'
        ? [row.employeeId, row.employee]
        : grouping === 'team'
          ? [row.teamId, row.team]
          : [String(row.periodNumber), row.periodLabel]
    const group = groups.get(key) ?? { key, label, entries: 0, hours: 0, gapHours: 0 }
    group.entries += 1
    group.hours += row.hours
    if ((row.shortBy ?? 0) > 0) group.gapHours += row.hours
    groups.set(key, group)
  }
  return Array.from(groups.values()).sort((a, b) =>
    grouping === 'period' ? Number(a.key) - Number(b.key) : a.label.localeCompare(b.label),
  )
}
